/**
 * Who it goes to, as what, and when — 17 Sep 2026.
 *
 * The second half of writing a newsletter, on its own screen. Everything here
 * is a decision about the send rather than about the words, which is why it is
 * not on the canvas: a writer choosing a paragraph and a sender choosing forty
 * thousand recipients are doing different jobs an hour apart.
 *
 * ## Audiences combine, and the count is the deduplicated one
 *
 * Tick the app users and the PRO companies and it is one send. The server
 * unions the lists and removes the overlap — somebody who guides professionally
 * and uses the app on their days off is one person with one inbox — so the
 * number under the picker is the number of messages that will leave, not the
 * sum of two lists.
 *
 * ## Marketing and service stay apart, and the line is enforced elsewhere
 *
 * Marketing reaches only an explicit yes. Service reaches people who never
 * answered, because a receipt, an expiry notice or a welcome that explains the
 * product has to arrive either way. The switch here changes which audiences are
 * even offered; `audience.ts` refuses the combination regardless of what this
 * screen sends, which is where the guarantee belongs.
 *
 * The line is about content, not about the label. "How to get started" is
 * service; "how to get started, and here is 20% off" is marketing wearing a
 * service label, and no code can tell the difference. What it can do is make
 * the choice explicit and visible in the count before anything is sent.
 *
 * ## Countries and languages — 27 Oct 2026
 *
 * A send can keep to the app accounts last seen in some countries, or to a
 * language Skida writes in. Both come from RevenueCat by way of
 * `skidaCrmCustomerFacts`; the language is derived from the country. The
 * filter is part of what a test unlocks, and the count under it is resolved
 * exactly, because no aggregation knows a country.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/features/auth/AuthProvider'
import { callFunction } from '@/shared/firebase/callFunction'
import { nf } from '@/shared/util/numbers'
import { scheduleNewsletter } from './repository/newsletterRepository'
import type { EmailBlock } from '@/types/emailBlocks'
import './Newsletter.css'

type Kind = 'marketing' | 'service'

interface ReachOptions {
  countries: { code: string; accounts: number; language: string }[]
  languages: string[]
}

const LANGUAGE_NAMES: Record<string, string> = {
  no: 'Norsk',
  sv: 'Svenska',
  de: 'Deutsch',
  fr: 'Français',
  it: 'Italiano',
  es: 'Español',
  en: 'English (all other countries)',
}

/** "NO" → "Norway", in the browser's own words. */
const countryNames = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' })
  } catch {
    return null
  }
})()
const countryName = (code: string) => countryNames?.of(code) ?? code

/** How many countries show before "All countries". */
const FIRST_COUNTRIES = 12

interface AudienceRow {
  audience: string
  label: string
  recipients?: number
  refused?: string
}

export function NewsletterSend({
  subject,
  html,
  blocks,
  draftId,
  onBack,
  onSent,
}: {
  subject: string
  html: string
  /**
   * The sections `html` was rendered from. Written with the schedule so the
   * newsletter reopens in the editor as it was built — 21 Oct 2026; scheduling
   * used to write none, and reopening one gave the whole email as one section.
   */
  blocks: EmailBlock[]
  /** The draft this send is of, so scheduling updates it rather than adding one. */
  draftId: string
  onBack: () => void
  onSent: () => void
}) {
  const { user } = useAuth()
  const [kind, setKind] = useState<Kind>('marketing')
  const [sender, setSender] = useState<'consumer' | 'pro'>('consumer')
  const [picked, setPicked] = useState<string[]>([])
  const [when, setWhen] = useState('')
  const [rows, setRows] = useState<AudienceRow[] | null>(null)
  const [options, setOptions] = useState<ReachOptions | null>(null)
  const [countries, setCountries] = useState<string[]>([])
  const [languages, setLanguages] = useState<string[]>([])
  const [allCountries, setAllCountries] = useState(false)
  /** The exact count with the filter applied; null while counting or unfiltered. */
  const [filtered, setFiltered] = useState<{ recipients: number; key: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** The last content+audience a test went to an internal address for. */
  const [tested, setTested] = useState('')
  /**
   * Where the test goes. The sender's own address to begin with, because that
   * is the common case and typing it every time is friction for nothing.
   */
  const [testTo, setTestTo] = useState('')

  /** What a test proves is true only while the content and the list are unchanged. */
  const reach = useMemo(
    () =>
      countries.length || languages.length
        ? {
            ...(countries.length ? { countries: countries.slice().sort() } : {}),
            ...(languages.length ? { languages: languages.slice().sort() } : {}),
          }
        : null,
    [countries, languages],
  )
  const reachSig = reach ? JSON.stringify(reach) : ''

  const signature = useMemo(
    () => [picked.slice().sort().join(','), kind, sender, subject, html, reachSig].join('|'),
    [picked, kind, sender, subject, html, reachSig],
  )

  const load = useCallback(() => {
    setRows(null)
    callFunction<{ audiences: AudienceRow[]; reachOptions?: ReachOptions }>('crmAudiencePreview', { query: { kind } })
      .then((d) => {
        setRows(d.audiences)
        if (d.reachOptions) setOptions(d.reachOptions)
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
  }, [kind])

  useEffect(load, [load])

  // The exact count once a filter is set — resolved on the server, so it takes
  // a little while; the key keeps a slow answer from overwriting a newer one.
  const countKey = reach && picked.length ? [kind, picked.slice().sort().join(','), reachSig].join('|') : ''
  useEffect(() => {
    if (!countKey || !reach) {
      setFiltered(null)
      return
    }
    let live = true
    setFiltered(null)
    callFunction<{ recipients: number }>('crmAudiencePreview', {
      query: {
        kind,
        audiences: picked.join(','),
        countries: (reach.countries ?? []).join(','),
        languages: (reach.languages ?? []).join(','),
      },
    })
      .then((d) => live && setFiltered({ recipients: d.recipients, key: countKey }))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)))
    return () => {
      live = false
    }
    // `countKey` holds everything the request reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countKey])

  const flip = (list: string[], set: (v: string[]) => void, x: string) =>
    set(list.includes(x) ? list.filter((y) => y !== x) : [...list, x])

  // Once the account is known, and never again — retyping over somebody who is
  // halfway through an address would be worse than an empty field.
  useEffect(() => {
    if (user?.email) setTestTo((t) => t || user.email!)
  }, [user?.email])

  // Anything the new kind refuses drops out of the selection rather than being
  // carried silently into a send the server would reject.
  useEffect(() => {
    if (!rows) return
    setPicked((p) => p.filter((k) => rows.some((r) => r.audience === k && r.refused === undefined)))
  }, [rows])

  const available = (rows ?? []).filter((r) => r.audience !== 'nobody')
  /**
   * The headline figure, and it is an upper bound until the server answers.
   *
   * Summing the picked audiences double-counts whoever is in two of them. The
   * exact number comes back from the send itself, which resolves and
   * deduplicates — so this is labelled as "up to" rather than quietly wrong.
   */
  const upperBound = picked.reduce(
    (n, k) => n + (available.find((r) => r.audience === k)?.recipients ?? 0),
    0,
  )

  const toggle = (key: string) =>
    setPicked((p) => (p.includes(key) ? p.filter((x) => x !== key) : [...p, key]))

  const sendTest = async () => {
    const to = testTo.trim().toLowerCase()
    setBusy(true)
    setError(null)
    setNote(null)
    try {
      const out = await callFunction<{ unlocks?: boolean; withRealData?: boolean }>(
        'crmSendCampaign',
        {
          method: 'POST',
          body: { audiences: picked, reach, kind, subject, html, sender, mode: 'test', testTo: to },
        },
      )
      // Only an internal address unlocks the send — see `unlocks` in
      // `sendCampaign.ts`. Recording the signature for any other one would let a
      // test to a customer stand in for the test this screen asks for.
      if (out.unlocks) setTested(signature)
      setNote(
        out.withRealData
          ? `Test sent to ${to}, with that account's own figures in the merge fields.`
          : `Test sent to ${to}. ${to} is not an app account, so the merge fields show example values.`,
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const schedule = async () => {
    const at = new Date(when)
    if (!when || Number.isNaN(at.getTime()) || at.getTime() <= Date.now()) {
      setError('Pick a time in the future.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await scheduleNewsletter(
        draftId || null,
        {
          subject,
          html,
          blocks,
          audience: picked[0] ?? '',
          audiences: picked,
          reach,
          kind,
          sender,
          savedBy: user?.email ?? '',
        },
        at,
      )
      setNote(`Scheduled for ${at.toLocaleString('nb-NO')}.`)
      onSent()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="nls">
      <header className="nlw__bar">
        <button className="btn btn--ghost btn--tiny" onClick={onBack}>
          ← Back to the message
        </button>
        <div className="nls__subject">{subject || 'Untitled'}</div>
      </header>

      <div className="nls__body">
        <section className="card nls__card">
          <div className="card-label">What kind of message is this?</div>
          <div className="nls__chips">
            <button className="chip" aria-pressed={kind === 'marketing'} onClick={() => setKind('marketing')}>
              Marketing
            </button>
            <button className="chip" aria-pressed={kind === 'service'} onClick={() => setKind('service')}>
              Service
            </button>
          </div>
          <p className="micro">
            {kind === 'marketing'
              ? 'Reaches only people who have explicitly opted in. Anyone who opted out or was never asked is excluded, and cannot be reached by any audience.'
              : 'Consent does not apply: a receipt, an expiry notice or a welcome that explains the product must arrive either way. Anything with an offer in it is marketing whatever this says.'}
          </p>
        </section>

        <section className="card nls__card">
          <div className="card-label">Who receives it</div>
          <p className="micro">
            Tick as many as apply — app and PRO in one send. Anybody in two of them gets one copy.
          </p>

          {rows === null ? (
            <p className="empty-note">Counting…</p>
          ) : (
            <div className="nls__audiences">
              {available.map((r) => (
                <label
                  className={`nls__audience${r.refused ? ' nls__audience--off' : ''}`}
                  key={r.audience}
                >
                  <input
                    type="checkbox"
                    checked={picked.includes(r.audience)}
                    disabled={Boolean(r.refused)}
                    onChange={() => toggle(r.audience)}
                  />
                  <span className="nls__audiencename">
                    {r.label}
                    {r.refused && <span className="micro">{r.refused}</span>}
                  </span>
                  <span className="nls__audiencecount">
                    {r.refused ? '—' : nf(r.recipients ?? 0)}
                  </span>
                </label>
              ))}
            </div>
          )}

          {picked.length > 0 &&
            (reach ? (
              <p className="nls__total">
                {filtered?.key === countKey ? (
                  <>
                    <strong>{nf(filtered.recipients)}</strong> recipients in the chosen countries and languages
                  </>
                ) : (
                  'Counting the chosen countries and languages…'
                )}
              </p>
            ) : (
              <p className="nls__total">
                Up to <strong>{nf(upperBound)}</strong> recipients
                {picked.length > 1 && ' — fewer once the overlap is removed'}
              </p>
            ))}
        </section>

        <section className="card nls__card">
          <div className="card-label">Where they are</div>
          <p className="micro">
            Leave both empty to send to everybody. Pick a language, countries, or both — both must
            hold. The country is where the app last saw the account (RevenueCat), and the language
            is the one Skida writes in for that country. Only app accounts with a known country are
            reached, so leads, ambassadors and partners drop out of a filtered send.
          </p>
          <div className="card-label">Language</div>
          <div className="nls__chips">
            {(options?.languages ?? Object.keys(LANGUAGE_NAMES)).map((l) => (
              <button
                key={l}
                className="chip"
                aria-pressed={languages.includes(l)}
                onClick={() => flip(languages, setLanguages, l)}
              >
                {LANGUAGE_NAMES[l] ?? l}
              </button>
            ))}
          </div>
          <div className="card-label">Countries</div>
          {options === null ? (
            <p className="empty-note">Reading countries…</p>
          ) : options.countries.length === 0 ? (
            <p className="empty-note">No countries known yet.</p>
          ) : (
            <div className="nls__chips">
              {(allCountries ? options.countries : options.countries.slice(0, FIRST_COUNTRIES))
                .concat(
                  // A picked country stays visible when the list is folded.
                  allCountries
                    ? []
                    : options.countries.slice(FIRST_COUNTRIES).filter((c) => countries.includes(c.code)),
                )
                .map((c) => (
                  <button
                    key={c.code}
                    className="chip"
                    aria-pressed={countries.includes(c.code)}
                    onClick={() => flip(countries, setCountries, c.code)}
                    title={`${nf(c.accounts)} accounts · written to in ${LANGUAGE_NAMES[c.language] ?? c.language}`}
                  >
                    {countryName(c.code)} · {nf(c.accounts)}
                  </button>
                ))}
              {options.countries.length > FIRST_COUNTRIES && (
                <button className="btn btn--ghost btn--tiny" onClick={() => setAllCountries((a) => !a)}>
                  {allCountries ? 'Fewer' : `All ${options.countries.length} countries`}
                </button>
              )}
            </div>
          )}
        </section>

        <section className="card nls__card">
          <div className="card-label">Sent from</div>
          <div className="nls__chips">
            <button className="chip" aria-pressed={sender === 'consumer'} onClick={() => setSender('consumer')}>
              Skida · post@news.skida.app
            </button>
            <button className="chip" aria-pressed={sender === 'pro'} onClick={() => setSender('pro')}>
              Skida PRO · post@skida.pro
            </button>
          </div>
          <p className="micro">
            Its own choice rather than something the audience decides. A note to guides reads as
            marketing if it arrives from the consumer address, and a note to app users reads as
            somebody else&rsquo;s product if it arrives from the PRO one.
          </p>
        </section>

        <section className="card nls__card">
          <div className="card-label">When</div>
          <input
            className="input nls__when"
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
          />
          <p className="micro">
            The audience is resolved when it sends, not now — so anybody who joins or opts out
            between here and then is counted correctly. Routes, collections, bookmarks and tracker
            connections are recounted at the same moment, and the send waits for that to finish.
          </p>
        </section>

        <section className="card nls__card">
          <div className="card-label">Test it</div>
          <div className="nls__testrow">
            <input
              className="input"
              type="email"
              value={testTo}
              placeholder="somebody@example.com"
              onChange={(e) => setTestTo(e.target.value)}
            />
            <button
              className="btn btn--ghost"
              disabled={busy || picked.length === 0 || !testTo.trim()}
              onClick={() => void sendTest()}
            >
              {busy ? 'Working…' : 'Send test'}
            </button>
          </div>
          <p className="micro">
            Any address. If it belongs to an app account the merge fields are filled with that
            person&rsquo;s own figures — routes, collections, subscription — and the sections are cut
            as they would really receive them. The counts are rebuilt just before it goes, so they
            are today&rsquo;s.
          </p>
          <p className="micro">
            Only a test to a <strong>@mountainsnow.ski</strong> address unlocks scheduling. Testing
            against a customer proves the merge; it is not the check this screen is asking for.
          </p>
        </section>

        {tested !== signature && (
          <p className="recform__note">
            Send a test to your own address first. It is tied to this exact subject, message,
            audience and kind, so any edit needs a fresh one.
          </p>
        )}
        {note && <p className="recform__note payreq__done">{note}</p>}
        {error && <p className="recform__error">{error}</p>}
      </div>

      <footer className="nls__foot">
        <button
          className="btn btn--primary"
          disabled={busy || picked.length === 0 || !when || tested !== signature}
          onClick={() => void schedule()}
        >
          Schedule
        </button>
      </footer>
    </div>
  )
}
